import { useEffect, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "@/utils/hooks/reactUseCustomHooks";
import {
  getPopularSearchData,
  setPopularItems,
  setPopularItemsStore,
} from "@/utils/redux/algolia";
import { useStoreDetails } from "./useStoreDetails";
import { getAlcoholStoreFilter } from "@/helpers/productsFilterHelper";

const inFlightRequests: Map<string, Promise<any>> = new Map();

export const useTrendingItems = (shouldFetch: boolean) => {
  const dispatch = useAppDispatch();
  const { storeID, storeRegion } = useStoreDetails();

  const popularItems = useAppSelector(
    (state) => state.algoliaSlice.popularItems
  );
  const popularItemsStore = useAppSelector(
    (state) => state.algoliaSlice.popularItemsStore
  );
  const isLoading = useAppSelector(
    (state) => state.algoliaSlice.loading === "pending"
  );

  const [data, setData] = useState(popularItems);

  useEffect(() => {
    const fetchDataIfNeeded = async () => {
      if (!shouldFetch || !storeID) return;
      if (popularItems && popularItemsStore === storeID) {
        setData(popularItems);
        return;
      }
      if (inFlightRequests.has(storeID)) {
        try {
          const existingPromise = inFlightRequests.get(storeID)!;
          const result = await existingPromise;
          setData(result);
        } catch (error) {
          console.error("Error from in-flight request:", error);
          setData(null);
        }
        return;
      }
      const productFilter = getAlcoholStoreFilter(storeID, storeRegion);
      const productFilterWithoutStore = productFilter.replace(
        new RegExp(`storeId:${storeID}\\sAND\\s`, "i"),
        ""
      );
      const requestPromise = dispatch(
        getPopularSearchData({
          storeID,
          filters: { product: productFilterWithoutStore },
        })
      )
        .unwrap()
        .then((result) => {
          dispatch(setPopularItems(result));
          dispatch(setPopularItemsStore(storeID));
          setData(result);
          return result;
        })
        .catch((error) => {
          console.error("Error fetching popular search data:", error);
          setData(null);
          throw error;
        })
        .finally(() => {
          inFlightRequests.delete(storeID);
        });
      inFlightRequests.set(storeID, requestPromise);

      try {
        await requestPromise;
      } catch (error) {
        console.warn("Request promise failed after being tracked:", error);
      }
    };

    fetchDataIfNeeded();
  }, [
    shouldFetch,
    dispatch,
    storeID,
    storeRegion,
    popularItems,
    popularItemsStore,
  ]);

  return {
    data,
    isLoading,
  };
};
